/**
 * Утилиты для работы с социальными ссылками в админке.
 * Определяет платформу по URL и нормализует ссылки.
 */

export type SocialPlatform =
  | 'telegram'
  | 'instagram'
  | 'twitter'
  | 'linkedin'
  | 'youtube'
  | 'tiktok'
  | 'facebook'
  | 'website';

/** Домены для определения платформы */
const PLATFORM_DOMAINS: ReadonlyArray<{ platform: SocialPlatform; domains: readonly string[] }> = [
  { platform: 'telegram', domains: ['t.me', 'telegram.me'] },
  { platform: 'instagram', domains: ['instagram.com'] },
  { platform: 'twitter', domains: ['twitter.com', 'x.com'] },
  { platform: 'linkedin', domains: ['linkedin.com'] },
  { platform: 'youtube', domains: ['youtube.com', 'youtu.be'] },
  { platform: 'tiktok', domains: ['tiktok.com'] },
  { platform: 'facebook', domains: ['facebook.com', 'fb.com'] },
];

/** Лейблы платформ для отображения в форме */
const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  telegram: 'Telegram',
  instagram: 'Instagram',
  twitter: 'X (Twitter)',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  website: 'Сайт',
};

function isFullUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

/**
 * Добавляет https:// если протокол отсутствует.
 */
export function ensureHttps(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {return trimmed;}
  if (isFullUrl(trimmed)) {return trimmed;}
  return `https://${trimmed}`;
}

/**
 * Определяет платформу по URL.
 */
export function detectPlatformFromUrl(url: string): SocialPlatform {
  const trimmed = url.trim();
  if (!trimmed) {return 'website';}

  try {
    const withProtocol = isFullUrl(trimmed) ? trimmed : `https://${trimmed}`;
    const hostname = new URL(withProtocol).hostname.replace(/^www\./, '');

    for (const { platform, domains } of PLATFORM_DOMAINS) {
      if (domains.some((d) => hostname === d)) {
        return platform;
      }
    }
  } catch {
    // не валидный URL — website
  }

  return 'website';
}

/**
 * Возвращает человекочитаемый лейбл платформы.
 */
export function getPlatformLabel(url: string): string {
  const platform = detectPlatformFromUrl(url);
  return PLATFORM_LABELS[platform];
}

/**
 * Конвертирует массив URL в Record<platform, url> для сохранения в БД.
 * Если несколько ссылок на одну платформу — добавляет индекс.
 */
export function urlsToSocialLinks(urls: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  const platformCounts: Record<string, number> = {};

  for (const raw of urls) {
    const url = ensureHttps(raw);
    if (!url) {continue;}

    const platform = detectPlatformFromUrl(url);
    const count = (platformCounts[platform] ?? 0) + 1;
    platformCounts[platform] = count;

    const key = count === 1 ? platform : `${platform}_${count}`;
    result[key] = url;
  }

  return result;
}
